import { LoadingSkeleton } from "./LoadingSkeleton";
import { useRef, useState } from "react";
import { ApiError, request } from "../api/client";
import { normalizeMobilePhone } from "../auth/mobilePhone";
import { useLocale } from "../i18n";
import { ErrorNotice } from "./AdminFeedback";
import { FieldRequirement } from "./FieldRequirement";
import { ValidatedForm } from "./ValidatedForm";
import { AppPhoneInput } from "./inputs/AppPhoneInput";
import "../styles/otp-counters.css";

type Counter = {
  version: number; maskedMobileNumber: string; hourCount: number; dayCount: number;
  hourLimit: number | null; dayLimit: number | null; recipientLimitsEnabled: boolean;
  blocked: boolean; nextAllowedAtEpochMillis: number | null; cooldownSeconds: number;
};
type Selection = { mobileNumber: string; countryCode: string };
type Reset = Selection & { requestId: string; expectedVersion: number; reason: string };

export function OtpCounters() {
  const { w, lang } = useLocale();
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("JO");
  const [counter, setCounter] = useState<Counter>();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const selection = useRef<Selection | null>(null);
  const pending = useRef<Reset | null>(null);
  const running = useRef(false);
  function change() { setCounter(undefined); selection.current = null; setSaved(false); setError(undefined); setPhoneError(""); }
  async function lookup() {
    if (running.current || pending.current) return;
    let mobileNumber: string;
    try { mobileNumber = normalizeMobilePhone(phone, country); }
    catch { setPhoneError(w("أدخل رقم هاتف صحيحاً للدولة المختارة.", "Enter a valid phone number for the selected country.")); return; }
    running.current = true; setBusy(true); setCounter(undefined); setError(undefined); setSaved(false);
    const input = {mobileNumber, countryCode: country};
    try { const result = await request<Counter>("/otpCounters/status", "POST", input); selection.current = input; setCounter(result); }
    catch (e) { selection.current = null; setError(e); }
    finally { running.current = false; setBusy(false); }
  }
  async function reset() {
    if (running.current || !counter || !selection.current) return;
    running.current = true; setBusy(true); setError(undefined); setSaved(false);
    pending.current ??= {...selection.current, requestId: crypto.randomUUID(), expectedVersion: counter.version, reason: reason.trim()};
    try {
      const result = await request<Counter>("/otpCounters/reset", "POST", pending.current);
      pending.current = null; setCounter(result); setReason(""); setSaved(true);
    } catch (e) {
      setError(e);
      if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
        pending.current = null;
        if (e.status === 409) { setCounter(undefined); selection.current = null; }
      }
    } finally { running.current = false; setBusy(false); }
  }
  return <section className="panel otp-counters" aria-labelledby="otp-counters-title">
    <header><h2 id="otp-counters-title">{w("رموز التحقق", "Verification codes")}</h2>
      <p>{w("افحص عداد إرسال رموز التحقق لرقم محدد، وأعد ضبطه عند الحاجة.", "Check the verification-code sending counter for a phone number and reset it when needed.")}</p></header>
    <ValidatedForm onSubmit={e => {e.preventDefault(); void lookup();}}>
      <fieldset disabled={busy || !!pending.current}>
        <label htmlFor="otp-counter-phone"><span className="field-caption">{w("رقم الهاتف", "Phone number")}<FieldRequirement required /></span></label>
        <AppPhoneInput id="otp-counter-phone" countryCode={country} onCountryChange={value => {change(); setCountry(value);}}
          value={phone} onChange={e => {change(); setPhone(e.target.value);}} required validationError={phoneError} />
        {phoneError && <p className="field-error" role="alert">{phoneError}</p>}
        <button className="secondary">{busy ? <LoadingSkeleton variant="inline" label={w("جارٍ التحميل…", "Loading…")} /> : w("عرض العداد", "View counter")}</button>
      </fieldset>
    </ValidatedForm>
    <ErrorNotice error={error} />
    {saved && <p className="success-box" role="status">{w("تمت إعادة ضبط عداد الرقم وتسجيل الإجراء. لم تُرسل رسالة جديدة.", "The phone counter was reset and the action recorded. No new message was sent.")}</p>}
    {counter && <div className="otp-counter-details">
      <h3><bdi dir="ltr">{counter.maskedMobileNumber}</bdi></h3>
      <p className={counter.blocked ? "warning-box" : "notice-box"}>{counter.blocked
        ? w("عداد الرقم يمنع إرسال رمز جديد حالياً.", "The phone counter currently blocks sending another code.")
        : w("لا يوجد حظر من عداد الرقم حالياً.", "The phone counter is not currently blocking delivery.")}</p>
      {!counter.recipientLimitsEnabled && <p>{w("حدود الساعة واليوم معطّلة في بيئة التطوير. تبقى مهلة الانتظار بين الطلبات.", "Hourly and daily recipient caps are disabled in Development. The delay between requests still applies.")}</p>}
      <dl><div><dt>{w("خلال الساعة", "Within the hour")}</dt><dd>{counter.hourCount}{counter.hourLimit !== null ? ` / ${counter.hourLimit}` : ""}</dd></div>
        <div><dt>{w("خلال 24 ساعة", "Within 24 hours")}</dt><dd>{counter.dayCount}{counter.dayLimit !== null ? ` / ${counter.dayLimit}` : ""}</dd></div>
        {counter.nextAllowedAtEpochMillis !== null && <div><dt>{w("موعد انتهاء الحظر", "Block ends")}</dt><dd>{new Intl.DateTimeFormat(lang === "ar" ? "ar-JO-u-nu-latn" : "en", {dateStyle:"medium",timeStyle:"short"}).format(counter.nextAllowedAtEpochMillis)}</dd></div>}</dl>
      <ValidatedForm onSubmit={e => {e.preventDefault(); void reset();}}>
        <fieldset disabled={busy || !!pending.current}>
          <label htmlFor="otp-counter-reason"><span className="field-caption">{w("سبب إعادة الضبط", "Reason for reset")}<FieldRequirement required /></span></label>
          <textarea id="otp-counter-reason" value={reason} onChange={e => setReason(e.target.value)} required maxLength={500} rows={3} />
          <p>{w("يُسجَّل السبب مع حساب المسؤول في سجل الإجراءات. تبقى الرموز الصادرة وجلسات الدخول كما هي.", "The reason and administrator are recorded in the audit log. Existing codes and login sessions are preserved.")}</p>
        </fieldset>
        <button className="primary" disabled={busy}>{busy ? w("جارٍ إعادة الضبط…", "Resetting…") : pending.current ? w("إعادة محاولة الضبط", "Retry reset") : w("إعادة ضبط العداد", "Reset counter")}</button>
      </ValidatedForm>
    </div>}
  </section>;
}
